import { useState } from "react";
import api from "../config/axios";
import {
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Stack,
  Spinner,
} from "@chakra-ui/react";
import { useRouter } from "next/router";

const CreateQuestionsModal = ({ isOpen, onClose, userId, companyId }) => {
  const [questionFormat, setQuestionFormat] = useState("");
  const [numQuestions, setNumQuestions] = useState("");
  const [role, setRole] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [sector, setSector] = useState("");
  const [skills, setSkills] = useState("");
  const [inputQuestion, setInputQuestion] = useState("");
  const [questionTypes, setQuestionTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const router = useRouter();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (numQuestions <= 0) {
      alert("Please enter a positive number of questions.");
      setLoading(false);
      return;
    }

    // Define different prompts for each question format
    let prompt = "";

    switch (questionFormat) {
      case "MCQ":
        prompt = `
        Create ${numQuestions} multiple-choice questions with answers, each with 4 options, one of which is the correct answer, on the topic of ${skills}. 
        The questions should be of ${difficulty} level and relevant to the role of ${role} in the ${
          sector ? sector : "relevant"
        } industry.
        Please format the response as:
        question_no, question_type, difficulty, question, answer, option_1, option_2, option_3, option_4
        ${inputQuestion ? `Additional criteria: ${inputQuestion}` : ""}
        `;
        break;

      case "FIB":
        prompt = `
        Create ${numQuestions} fill-in-the-blank questions on the topic of ${skills}. 
        The questions should be of ${difficulty} level and relevant to the role of ${role} in the ${
          sector ? sector : "relevant"
        } industry.
        Please format the response as:
        question_no, question_type, difficulty, question, answer
        ${inputQuestion ? `Additional criteria: ${inputQuestion}` : ""}
        `;
        break;

      case "Coding":
        prompt = `
        Create ${numQuestions} coding questions on the topic of ${skills}. 
        The questions should be of ${difficulty} level and focus on ${skills}. The questions should be relevant to the role of ${role}.
        Please format the response as:
        question_no, question_type, difficulty, question, answer, output
        ${inputQuestion ? `Additional criteria: ${inputQuestion}` : ""}
        `;
        break;

      case "Descriptive":
        prompt = `
        Create ${numQuestions} descriptive questions on the topic of ${skills}. 
        The questions should be of ${difficulty} level and relevant to the role of ${role} in the ${
          sector ? sector : "relevant"
        } industry. 
        The answers should be brief and to the point (2-3 lines).
        Please format the response as follows:
        - question_no: Include a unique question number for each question.
        - question_type: Use "Descriptive" as the question type.
        - difficulty: Specify the difficulty as "${difficulty}".
        - question: Provide the question text.
        - answer: Provide a brief answer to each question.
        ${inputQuestion ? `Additional criteria: ${inputQuestion}` : ""}
        `;
        break;

      default:
        alert("Please select a valid question format.");
        setLoading(false);
        return;
    }

    const payload = {
      prompt,
      user_id: userId,
      company_id: companyId,
    };

    // In CreateQuestionsModal.js, in the handleSubmit function:
    try {
      // First API call to generate_text
      const generateTextResponse = await api.post("/generate_text/", payload);
    
      // Second API call to transform the payload using axios
      const transformResponse = await api.post("/transform_payload", generateTextResponse.data);
    
      const transformedData = transformResponse.data; // Axios returns data directly
      setLoading(false);
    
      // Navigate with the transformed data
      router.push({
        pathname: "/AiGeneratedQuestions",
        query: {
          data: JSON.stringify(transformedData), // This contains the {data: [...]} structure
          companyId: companyId,
          skills: `"${skills}"`
          ,
        },
      });
    } 
    
    catch (error) {
      console.error("Error in API calls:", error);
      setLoading(false);
      alert("An error occurred while generating questions. Please try again.");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Questions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={handleSubmit}>
            <FormControl id="num-questions" mb={4} isRequired>
              <FormLabel>Number of Questions</FormLabel>
              <Input
                type="number"
                placeholder="Enter number of questions"
                value={numQuestions}
                onChange={(e) => setNumQuestions(e.target.value)}
              />
            </FormControl>

            <FormControl id="question-format" mb={4} isRequired>
              <FormLabel>Question Format</FormLabel>
              <Select
                placeholder="Select question format"
                value={questionFormat}
                onChange={(e) => setQuestionFormat(e.target.value)}
              >
                <option value="MCQ">Multiple Choice Questions (MCQ)</option>
                <option value="FIB">Fill in the Blanks (FIB)</option>
                <option value="Coding">Coding</option>
                <option value="Descriptive">Descriptive</option>
              </Select>
            </FormControl>

            <FormControl id="role" mb={4} isRequired>
              <FormLabel>Role/Responsibility</FormLabel>
              <Input
                type="text"
                placeholder="Enter role/responsibility"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              />
            </FormControl>

            <FormControl id="question-types" mb={4}>
              <FormLabel>Question Type</FormLabel>
              <CheckboxGroup value={questionTypes} onChange={setQuestionTypes}>
                <Stack direction="column">
                  <Checkbox value="Problem solving">Problem solving</Checkbox>
                  <Checkbox value="Situational">Situational</Checkbox>
                  <Checkbox value="Scenario based">Scenario based</Checkbox>
                  <Checkbox value="Code Debugging">Code Debugging</Checkbox>
                  <Checkbox value="Conceptual">Conceptual</Checkbox>
                  <Checkbox value="Process">Process</Checkbox>
                  <Checkbox value="Logical">Logical</Checkbox>
                </Stack>
              </CheckboxGroup>
            </FormControl>

            <FormControl id="difficulty" mb={4} isRequired>
              <FormLabel>Difficulty</FormLabel>
              <Select
                placeholder="Select difficulty"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
              >
                <option value="Easy">Easy</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </Select>
            </FormControl>

            <FormControl id="sector" mb={4}>
              <FormLabel>Sector (Optional)</FormLabel>
              <Input
                type="text"
                placeholder="Enter sector (optional)"
                value={sector}
                onChange={(e) => setSector(e.target.value)}
              />
            </FormControl>

            <FormControl id="skills" mb={4} isRequired>
              <FormLabel>Skills</FormLabel>
              <Input
                type="text"
                placeholder="Enter skills"
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
              />
            </FormControl>

            <FormControl id="input-question" mb={4}>
              <FormLabel>Criteria (Optional)</FormLabel>
              <Textarea
                placeholder="Enter additional criteria (optional)"
                value={inputQuestion}
                onChange={(e) => setInputQuestion(e.target.value)}
              />
            </FormControl>

            <Button
              type="submit"
              colorScheme="blue"
              mr={3}
              isDisabled={loading}
            >
              {loading ? <Spinner size="sm" /> : "Submit"}
            </Button>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateQuestionsModal;
